import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import {
  fetchExportRegister,
  useAgeing,
  useGstThreshold,
  useRevenue,
  type GstLevel,
  type RevenueBy,
} from "@/api/reports";
import { CashPanel } from "@/components/reports/CashPanel";
import { ProfitPanel } from "@/components/reports/ProfitPanel";
import { RequestError } from "@/components/RequestError";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField, TextField } from "@/components/ui/field";
import { PageHeader } from "@/components/ui/page-header";
import { StatTile } from "@/components/ui/stat-tile";
import { TabPanel, Tabs, pickTab, type TabItem } from "@/components/ui/tabs";
import { fyCode, todayIso } from "@/lib/dates";
import { saveBlob } from "@/lib/files";
import { formatDate, formatInr, formatMoney } from "@/lib/format";
import { formatTotals } from "@/lib/money";

const TABS = ["ageing", "revenue", "profit", "cash", "gst", "export"] as const;
type ReportTab = (typeof TABS)[number];
const TAB_ITEMS: TabItem<ReportTab>[] = [
  { id: "ageing", label: "Ageing" },
  { id: "revenue", label: "Revenue" },
  { id: "profit", label: "Profit" },
  { id: "cash", label: "Cash" },
  { id: "gst", label: "GST threshold" },
  { id: "export", label: "For the CA" },
];

const REVENUE_OPTIONS = [
  { value: "client", label: "Client" },
  { value: "vessel", label: "Vessel" },
  { value: "month", label: "Month" },
];

const GST_TONE: Record<GstLevel, { variant: "success" | "warning" | "error"; title: string }> = {
  ok: { variant: "success", title: "Well under the registration threshold" },
  watch: { variant: "warning", title: "Past 75% of the registration threshold" },
  high: { variant: "warning", title: "Past 90% - talk to your CA about registering" },
  exceeded: { variant: "error", title: "Over the threshold - GST registration is due" },
};

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[0.8125rem] font-medium text-muted-foreground">{label}</p>
      <p className="mt-0.5 break-words font-display text-xl font-semibold text-foreground">
        {value}
      </p>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function AgeingPanel() {
  const [asOf, setAsOf] = useState("");
  const ageing = useAgeing({ asOf: asOf || undefined });

  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="ageing-as-of"
        label="As of"
        type="date"
        value={asOf}
        onChange={setAsOf}
        hint="Leave empty for today"
        className="max-w-[12rem]"
      />
      {ageing.isPending ? (
        <p className="text-muted-foreground">Working out what is owed…</p>
      ) : ageing.isError ? (
        <RequestError error={ageing.error} />
      ) : (
        <>
          <p className="text-sm">
            Outstanding on {formatDate(ageing.data.as_of)}:{" "}
            <strong>{formatTotals(ageing.data.outstanding)}</strong>
          </p>
          {ageing.data.late_charges.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Late charge accrued under SLA 6.5, not yet billed:{" "}
              <strong className="text-foreground">{formatTotals(ageing.data.late_charges)}</strong>
            </p>
          )}
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ageing.data.buckets.map((bucket) => (
              <li key={bucket.key}>
                <StatTile
                  className="h-full"
                  label={bucket.label}
                  value={formatTotals(bucket.totals)}
                  hint={bucket.invoices === 1 ? "1 invoice" : `${bucket.invoices} invoices`}
                />
              </li>
            ))}
          </ul>
          {ageing.data.rows.length === 0 ? (
            <EmptyState title="Nothing outstanding" description="Every issued invoice is settled." />
          ) : (
            <div className="table-frame">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Client</th>
                    <th>Vessel</th>
                    <th>Due</th>
                    <th className="text-right">Days late</th>
                    <th className="text-right">Late charge</th>
                    <th className="text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ageing.data.rows.map((row) => (
                    <tr key={row.invoice_id}>
                      <td className="whitespace-nowrap font-medium">
                        <Link
                          to={`/invoices/${row.invoice_id}`}
                          className="text-primary hover:underline"
                        >
                          {row.invoice_no ?? "Draft"}
                        </Link>
                      </td>
                      <td>{row.client_code}</td>
                      <td>{row.vessel_name ?? "—"}</td>
                      <td className="whitespace-nowrap">
                        {row.awaiting_bank_letter ? (
                          <Badge variant="muted">Bank letter pending</Badge>
                        ) : (
                          formatDate(row.effective_due_date)
                        )}
                      </td>
                      <td className="text-right">
                        {row.days_overdue > 0 ? row.days_overdue : "—"}
                      </td>
                      <td className="whitespace-nowrap text-right text-muted-foreground">
                        {Number(row.late_charge) > 0
                          ? formatMoney(row.late_charge, row.currency)
                          : "—"}
                      </td>
                      <td className="whitespace-nowrap text-right font-medium">
                        {formatMoney(row.balance, row.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RevenuePanel() {
  const [by, setBy] = useState<RevenueBy>("client");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const revenue = useRevenue({ by, from: from || undefined, to: to || undefined });

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          id="revenue-by"
          label="Group by"
          value={by}
          onChange={(value) => setBy(value as RevenueBy)}
          options={REVENUE_OPTIONS}
        />
        <TextField id="revenue-from" label="From" type="date" value={from} onChange={setFrom} />
        <TextField id="revenue-to" label="To" type="date" value={to} onChange={setTo} />
      </div>
      {revenue.isPending ? (
        <p className="text-muted-foreground">Adding up the invoices…</p>
      ) : revenue.isError ? (
        <RequestError error={revenue.error} />
      ) : revenue.data.rows.length === 0 ? (
        <EmptyState
          title="No invoices in this period"
          description="Issued invoices appear here; cancelled ones never count."
        />
      ) : (
        <>
          <p className="text-sm">
            Invoiced <strong>{formatTotals(revenue.data.totals)}</strong> · credited{" "}
            <strong>{formatInr(revenue.data.inr_credited)}</strong>
          </p>
          <div className="table-frame">
            <table className="data-table">
              <thead>
                <tr>
                  <th>
                    {by === "month" ? "Month" : by === "vessel" ? "Vessel" : "Client"}
                  </th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Invoiced</th>
                  <th className="text-right">Received</th>
                  <th className="text-right">Outstanding</th>
                  <th className="text-right">INR credited</th>
                </tr>
              </thead>
              <tbody>
                {revenue.data.rows.map((row) => (
                  <tr key={`${row.key}-${row.currency}`}>
                    <td className="font-medium">{row.label}</td>
                    <td className="text-right">{row.invoices}</td>
                    <td className="whitespace-nowrap text-right">
                      {formatMoney(row.invoiced, row.currency)}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {formatMoney(row.paid, row.currency)}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {formatMoney(row.balance, row.currency)}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {Number(row.inr_credited) > 0 ? formatInr(row.inr_credited) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function GstPanel() {
  const [fy, setFy] = useState("");
  const gst = useGstThreshold(fy || undefined);

  return (
    <div className="flex flex-col gap-4">
      <TextField
        id="gst-fy"
        label="Financial year"
        value={fy}
        onChange={setFy}
        hint="e.g. 26-27; leave empty for the current year"
        className="max-w-[12rem]"
      />
      {gst.isPending ? (
        <p className="text-muted-foreground">Adding up turnover…</p>
      ) : gst.isError ? (
        <RequestError error={gst.error} />
      ) : (
        <>
          <Alert variant={GST_TONE[gst.data.level].variant} title={GST_TONE[gst.data.level].title}>
            {formatInr(gst.data.turnover_inr)} of {formatInr(gst.data.threshold_inr)} in FY{" "}
            {gst.data.fy_code} ({gst.data.percentage}%).
          </Alert>
          <div
            role="meter"
            aria-label="Turnover against the GST threshold"
            aria-valuenow={gst.data.percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-3 w-full overflow-hidden rounded-full bg-muted"
          >
            <div
              className={
                gst.data.level === "exceeded" || gst.data.level === "high"
                  ? "h-full bg-destructive"
                  : gst.data.level === "watch"
                    ? "h-full bg-warning"
                    : "h-full bg-success"
              }
              style={{ width: `${Math.min(gst.data.percentage, 100)}%` }}
            />
          </div>
          <Card>
            <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Figure label="Counted for GST" value={formatInr(gst.data.turnover_inr)} />
              <Figure
                label="Credited by the bank"
                value={formatInr(gst.data.realised_inr)}
                hint="From the eFIRA"
              />
              <Figure
                label="Not realised yet"
                value={formatInr(gst.data.estimated_inr)}
                hint={
                  gst.data.average_rate
                    ? `At the average rate ${gst.data.average_rate}`
                    : "No rate yet"
                }
              />
              <Figure
                label="Invoiced"
                value={formatTotals(gst.data.invoiced)}
                hint={gst.data.invoices === 1 ? "1 invoice" : `${gst.data.invoices} invoices`}
              />
            </CardContent>
          </Card>
          <p className="text-sm text-muted-foreground">{gst.data.basis}</p>
          <p className="text-sm text-muted-foreground">
            Your CA confirms which threshold applies and how unrealised invoices should be valued.
            Change the limit under Settings → Company.
          </p>
        </>
      )}
    </div>
  );
}

function ExportPanel() {
  const [fy, setFy] = useState(fyCode(todayIso()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function download(): void {
    setBusy(true);
    setError(null);
    fetchExportRegister(fy || undefined)
      .then((file) => saveBlob(file.blob, file.filename))
      .catch((failure: unknown) => setError(failure))
      .finally(() => setBusy(false));
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <p className="text-sm text-muted-foreground">
        One workbook for your CA, with three sheets: every invoice of the year (including
        cancelled ones, marked as such) with its realisation deadline, every receipt with its
        eFIRA details, and a summary of the totals and how the rupee figures were reached.
      </p>
      <TextField
        id="export-fy"
        label="Financial year"
        value={fy}
        onChange={setFy}
        hint="e.g. 26-27"
        className="max-w-[12rem]"
      />
      <RequestError error={error} />
      <div>
        <Button onClick={download} disabled={busy}>
          <Download className="h-4 w-4" aria-hidden />
          {busy ? "Preparing…" : "Download the workbook"}
        </Button>
      </div>
    </div>
  );
}

export function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const tab = pickTab(params.get("report"), TABS, "ageing");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Reports"
        description="What is owed, what has been earned and kept, and the figures your CA needs."
      />
      <div>
        <Tabs
          label="Reports"
          items={TAB_ITEMS}
          value={tab}
          onChange={(next) => setParams(next === "ageing" ? {} : { report: next }, { replace: true })}
        />
        <TabPanel id={tab}>
          {tab === "ageing" && <AgeingPanel />}
          {tab === "revenue" && <RevenuePanel />}
          {tab === "profit" && <ProfitPanel />}
          {tab === "cash" && <CashPanel />}
          {tab === "gst" && <GstPanel />}
          {tab === "export" && <ExportPanel />}
        </TabPanel>
      </div>
    </div>
  );
}
